import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, ArrowRight, Search,
  Code, Shield, Smartphone, BrainCircuit, Palette, Tag,
} from 'lucide-react';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../../utils/constants';

const categoryIcons = {
  Engineering: Code,
  Mobile: Smartphone,
  Security: Shield,
  AI: BrainCircuit,
  Design: Palette,
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = useMemo(() => {
    let posts = [...BLOG_POSTS];
    if (activeCategory !== 'All') {
      posts = posts.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return posts;
  }, [activeCategory, searchQuery]);

  const featured = BLOG_POSTS[0];
  const FeaturedIcon = categoryIcons[featured.category] || Tag;

  return (
    <div className="r-root blog-page">
      <div className="r-page-bg" aria-hidden="true" />
      <div className="r-wrap">
        <div style={{ maxWidth: 720 }}>
          <div className="r-tag">Blog</div>
          <h1 className="r-head">
            Insights &amp; <span className="r-gtxt">engineering.</span>
          </h1>
          <p className="r-sub">
            Technical deep-dives, lessons from shipping, and thoughts on building software with AI.
          </p>
        </div>

        {/* Featured */}
        <div
          className="blog-featured r-panel"
          onClick={() => navigate(`/blog/${featured.id}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/blog/${featured.id}`); }}
        >
          <div className="blog-featured-visual">
            <FeaturedIcon className="w-12 h-12" style={{ color: 'var(--accent)', opacity: 0.7 }} />
          </div>
          <div className="blog-featured-body">
            <div className="blog-meta">
              <span className="blog-badge">{featured.category}</span>
              <span><Calendar className="w-3 h-3" style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />{formatDate(featured.date)}</span>
              <span><Clock className="w-3 h-3" style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />{featured.readTime}</span>
            </div>
            <h2 className="blog-featured-title">{featured.title}</h2>
            <p className="blog-featured-excerpt">{featured.excerpt}</p>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: 'var(--accent-light)' }}>
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="blog-controls">
          <div className="blog-cats">
            {BLOG_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`blog-cat ${activeCategory === cat ? 'is-on' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="blog-search">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search articles"
            />
          </div>
        </div>

        {/* Grid */}
        <div className="blog-grid">
          {filteredPosts.map((post) => {
            const CatIcon = categoryIcons[post.category] || Tag;
            return (
              <article
                key={post.id}
                className="blog-card"
                onClick={() => navigate(`/blog/${post.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/blog/${post.id}`); }}
              >
                <div className="blog-card-visual"><CatIcon className="w-8 h-8" /></div>
                <div className="blog-card-body">
                  <div className="blog-meta">
                    <span className="blog-badge">{post.category}</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="blog-card-title">{post.title}</h3>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <div className="blog-card-foot">
                    <span>{post.author} · {formatDate(post.date)}</span>
                    <ArrowRight className="w-4 h-4" style={{ color: 'var(--accent-light)' }} />
                  </div>
                </div>
              </article>
            );
          })}

          {filteredPosts.length === 0 && (
            <div className="blog-empty">
              <p>No articles found. Try a different filter.</p>
              <button
                type="button"
                className="btn-secondary fh-cta"
                style={{ marginTop: 16 }}
                onClick={() => { setActiveCategory('All'); setSearchQuery(''); }}
              >
                <span>Reset filters</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
